/* Loomish — The Archive
 * A cross-collection pool of the catalogue is rendered server-side; on every
 * load this shuffles it and keeps N, so each visit mixes different pieces from
 * different collections. Also drives drag / swipe / arrow-key nav, a progress
 * thread and the unveil-on-scroll choreography.
 */
(function () {
  if (customElements.get('loomish-archive')) return;

  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  class LoomishArchive extends HTMLElement {
    connectedCallback() {
      this.root = this.querySelector('[data-lmar-root]');
      this.rail = this.querySelector('[data-lmar-rail]');
      this.foot = this.querySelector('[data-lmar-foot]');
      this.thumb = this.querySelector('[data-lmar-thumb]');
      this.prevBtn = this.querySelector('[data-lmar-prev]');
      this.nextBtn = this.querySelector('[data-lmar-next]');
      this._active = false;

      // Reshuffle the cross-collection pool and keep N — fresh mix every load.
      this._shuffleTrim();

      if (!this.rail.querySelector('.lmar__card')) {
        this.setAttribute('hidden', '');
        return;
      }

      this._bindControls();
      this._bindDrag();
      this._bindKeys();

      this._onScroll = this._onScroll.bind(this);
      this._onResize = this._debounce(this._updateFoot.bind(this), 150);
      this.rail.addEventListener('scroll', this._onScroll, { passive: true });
      window.addEventListener('resize', this._onResize);

      this.io = new IntersectionObserver(
        (entries, obs) => {
          if (!entries[0] || !entries[0].isIntersecting) return;
          obs.disconnect();
          this._reveal();
        },
        { rootMargin: '0px 0px 300px 0px' }
      );
      this.io.observe(this);

      this._updateFoot();
    }

    disconnectedCallback() {
      this.io && this.io.disconnect();
      window.removeEventListener('resize', this._onResize);
      document.removeEventListener('keydown', this._onKey);
      if (this._inertia) cancelAnimationFrame(this._inertia);
    }

    _shuffleTrim() {
      var count = parseInt(this.dataset.count, 10) || 8;
      var cards = Array.prototype.slice.call(this.rail.querySelectorAll('.lmar__card'));
      // Fisher–Yates
      for (var i = cards.length - 1; i > 0; i--) {
        var j = (Math.random() * (i + 1)) | 0;
        var t = cards[i];
        cards[i] = cards[j];
        cards[j] = t;
      }
      // Drop the overflow before their lazy images ever load, keep N, reorder.
      cards.slice(count).forEach(function (c) {
        if (c.parentNode) c.parentNode.removeChild(c);
      });
      var frag = document.createDocumentFragment();
      cards.slice(0, count).forEach(function (c, idx) {
        c.style.setProperty('--i', idx);
        frag.appendChild(c);
      });
      this.rail.appendChild(frag);
    }

    _reveal() {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this.root.classList.add('is-in');
          this._updateFoot();
        });
      });
    }

    _step() {
      var card = this.rail.querySelector('.lmar__card');
      var gap = parseFloat(getComputedStyle(this.rail).columnGap) || 20;
      return card ? card.getBoundingClientRect().width + gap : this.rail.clientWidth * 0.8;
    }

    _scrollByCard(dir) {
      this.rail.scrollBy({ left: dir * this._step(), behavior: REDUCED ? 'auto' : 'smooth' });
    }

    _bindControls() {
      var self = this;
      this.prevBtn && this.prevBtn.addEventListener('click', function () { self._scrollByCard(-1); });
      this.nextBtn && this.nextBtn.addEventListener('click', function () { self._scrollByCard(1); });
    }

    _bindKeys() {
      var self = this;
      this.addEventListener('pointerenter', function () { self._active = true; });
      this.addEventListener('pointerleave', function () { self._active = false; });
      this.addEventListener('focusin', function () { self._active = true; });
      this.addEventListener('focusout', function () { self._active = false; });

      this._onKey = function (e) {
        if (!self._active) return;
        if (e.key === 'ArrowRight') { e.preventDefault(); self._scrollByCard(1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); self._scrollByCard(-1); }
      };
      document.addEventListener('keydown', this._onKey);
    }

    _bindDrag() {
      var self = this;
      var rail = this.rail;
      var down = false, startX = 0, startLeft = 0, moved = 0, pid = null;
      var vx = 0, lastX = 0, lastT = 0;

      rail.addEventListener('pointerdown', function (e) {
        if (e.pointerType === 'touch') return; // native momentum on touch
        if (self._inertia) cancelAnimationFrame(self._inertia);
        down = true;
        moved = 0;
        vx = 0;
        startX = e.clientX;
        startLeft = rail.scrollLeft;
        lastX = e.clientX;
        lastT = performance.now();
        pid = e.pointerId;
        try { rail.setPointerCapture(pid); } catch (_) {}
        rail.classList.add('is-drag');
      });

      rail.addEventListener('pointermove', function (e) {
        if (!down) return;
        var dx = e.clientX - startX;
        moved += Math.abs(dx);
        rail.scrollLeft = startLeft - dx;
        startX = e.clientX;
        startLeft = rail.scrollLeft;

        var now = performance.now();
        var dt = now - lastT;
        if (dt > 0) vx = (e.clientX - lastX) / dt; // px per ms
        lastX = e.clientX;
        lastT = now;
      });

      var end = function () {
        if (!down) return;
        down = false;
        rail.classList.remove('is-drag');
        try { pid != null && rail.releasePointerCapture(pid); } catch (_) {}

        // Inertial glide — decays smoothly so the rail feels native.
        if (REDUCED || Math.abs(vx) < 0.05) return;
        var v = vx * 16; // seed velocity (px/frame)
        var glide = function () {
          v *= 0.94;
          rail.scrollLeft -= v;
          var max = rail.scrollWidth - rail.clientWidth;
          if (rail.scrollLeft <= 0 || rail.scrollLeft >= max || Math.abs(v) < 0.35) {
            self._inertia = null;
            return;
          }
          self._inertia = requestAnimationFrame(glide);
        };
        self._inertia = requestAnimationFrame(glide);
      };
      rail.addEventListener('pointerup', end);
      rail.addEventListener('pointercancel', end);
      rail.addEventListener('pointerleave', end);

      rail.addEventListener(
        'click',
        function (e) {
          if (moved > 8) { e.preventDefault(); e.stopPropagation(); moved = 0; }
        },
        true
      );
    }

    _onScroll() {
      var max = this.rail.scrollWidth - this.rail.clientWidth;
      var p = max > 0 ? this.rail.scrollLeft / max : 0;

      if (this.thumb) {
        var track = this.thumb.parentElement;
        var travel = track.clientWidth - this.thumb.offsetWidth;
        this.thumb.style.transform = 'translateX(' + Math.max(0, p * travel) + 'px)';
      }
      if (this.prevBtn) this.prevBtn.toggleAttribute('disabled', this.rail.scrollLeft <= 1);
      if (this.nextBtn) this.nextBtn.toggleAttribute('disabled', this.rail.scrollLeft >= max - 1);
    }

    _updateFoot() {
      if (!this.foot) return;
      var scrollable = this.rail.scrollWidth - this.rail.clientWidth > 4;
      this.foot.style.display = scrollable ? '' : 'none';
      if (scrollable && this.thumb) {
        var track = this.thumb.parentElement;
        var ratio = this.rail.clientWidth / this.rail.scrollWidth;
        this.thumb.style.width = Math.max(ratio * track.clientWidth, 34) + 'px';
      }
      this._onScroll();
    }

    _debounce(fn, wait) {
      var t;
      return function () { clearTimeout(t); t = setTimeout(fn, wait); };
    }
  }

  customElements.define('loomish-archive', LoomishArchive);
})();
